import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const AboutPage = () => {
  const navigate = useNavigate();
  const [announcementVisible, setAnnouncementVisible] = useState(true);

  const [heroRef, heroInView] = useInView(0.05);
  const [statsRef, statsInView] = useInView();
  const [solveRef, solveInView] = useInView();
  const [philRef, philInView] = useInView();
  const [compareRef, compareInView] = useInView();
  const [industriesRef, industriesInView] = useInView();
  const [valuesRef, valuesInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  const problems = [
    { icon: 'data_object', title: 'Data Silos', description: 'Breaking down barriers between departments with a unified data layer that connects every workflow in real-time.' },
    { icon: 'error_outline', title: 'Manual Entry Errors', description: 'Eliminating human error through automated machine-learning workflows that handle complex data processing.' },
    { icon: 'device_hub', title: 'Disconnected Systems', description: 'Replacing fragmented legacy tools with a single, cohesive operating system designed for the AI era.' },
  ];

  const philosophyPillars = [
    { icon: 'psychology', title: 'AI-First Thinking', desc: 'We design for machines to execute and humans to supervise.' },
    { icon: 'hub', title: 'Unified Architecture', desc: 'One platform, one data layer, infinite possibilities.' },
    { icon: 'trending_up', title: 'Compounding Growth', desc: 'Every automation compounds — the more you automate, the faster you grow.' },
  ];

  const whatWeDo = [
    'End-to-end business automation through neural orchestration',
    'Unified AI-First operating system for the entire enterprise',
    'Autonomous decision-making via predictive analytics',
  ];

  const whatWeDont = [
    'Building simple task-based bots or basic macros',
    'Providing fragmented plugins for legacy software suites',
    'Selling user data or providing "black box" insights',
  ];

  const industries = [
    { icon: 'account_balance', name: 'Fintech', desc: 'Intelligent risk modeling and automated compliance workflows.' },
    { icon: 'local_shipping', name: 'Logistics', desc: 'Real-time route optimization and autonomous dispatch systems.' },
    { icon: 'precision_manufacturing', name: 'Manufacturing', desc: 'Predictive maintenance and AI-driven production scheduling.' },
    { icon: 'medical_services', name: 'Healthcare', desc: 'Patient data orchestration and clinical decision support.' },
    { icon: 'storefront', name: 'Retail & Dining', desc: 'Smart inventory, demand forecasting, and guest experience AI.' },
    { icon: 'movie', name: 'Media & Entertainment', desc: 'Automated content pipelines and intelligent distribution.' },
  ];

  const values = [
    { icon: 'verified_user', title: 'Integrity', desc: 'We build trust through transparency in everything we do.' },
    { icon: 'bolt', title: 'Speed', desc: 'We move fast without breaking things — reliability is non-negotiable.' },
    { icon: 'groups', title: 'Collaboration', desc: 'The best solutions emerge from diverse perspectives working together.' },
    { icon: 'auto_awesome', title: 'Innovation', desc: 'We challenge the status quo and push the boundaries of what AI can do.' },
  ];

  return (
    <div className="bg-dark text-white overflow-hidden font-display">
      {announcementVisible && (
        <div className="bg-primary relative z-50">
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-medium text-white">
            <span className="animate-pulse">✦</span>
            <span>OrbitMind 2.0 is here — the most powerful AI operating system yet.</span>
            <Link to="/announcement" className="underline underline-offset-2 hover:opacity-80 transition-opacity font-semibold whitespace-nowrap">Learn more →</Link>
            <button onClick={() => setAnnouncementVisible(false)} className="absolute right-4 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100 transition-opacity" aria-label="Dismiss">
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          </div>
        </div>
      )}
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[90vh] flex flex-col justify-center overflow-hidden bg-dark">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(rgba(19,164,236,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(19,164,236,0.15) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
          <div className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full opacity-20 blur-3xl" style={{ background: 'radial-gradient(circle, #13a4ec 0%, transparent 70%)' }} />
          <div className="absolute top-1/3 -right-60 w-[600px] h-[600px] rounded-full opacity-15 blur-3xl" style={{ background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)' }} />
        </div>
        <div ref={heroRef} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 lg:py-36 text-center">
          <div className={`inline-flex items-center gap-2 bg-white/5 border border-primary/30 rounded-full px-4 py-2 mb-8 backdrop-blur-sm transition-all duration-700 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <span className="material-symbols-outlined text-primary text-base">auto_stories</span>
            <span className="text-sm font-semibold text-primary">Our Story</span>
          </div>
          <h1 className={`text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight mb-6 transition-all duration-700 delay-100 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-white">Centralizing global business through</span><br />
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #13a4ec 0%, #7c3aed 50%, #ec4899 100%)' }}>autonomous intelligence.</span>
          </h1>
          <p className={`text-lg lg:text-xl text-gray-400 leading-relaxed mb-10 max-w-2xl mx-auto transition-all duration-700 delay-200 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            OrbitMind was founded on a single conviction: the enterprise of tomorrow will be built on autonomous intelligence. We provide the centralized AI operating system that replaces manual inefficiencies, connects every workflow, and compounds growth — all from one platform.
          </p>
          <div className={`flex flex-wrap gap-4 justify-center transition-all duration-700 delay-300 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <Link to="/services" className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white text-base transition-all duration-300 hover:scale-105 hover:shadow-2xl" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)', boxShadow: '0 0 30px rgba(19,164,236,0.4)' }}>
              <span>Explore Services</span>
              <span className="material-symbols-outlined text-lg transition-transform group-hover:translate-x-1">arrow_forward</span>
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white text-base border border-white/20 bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-primary/50 transition-all duration-300 hover:scale-105">
              <span className="material-symbols-outlined text-lg text-primary">mail</span>
              <span>Get in Touch</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section ref={statsRef} className="bg-[#0b1620] border-y border-white/5 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid grid-cols-2 lg:grid-cols-4 gap-8 transition-all duration-1000 ${statsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {[{ label: 'Founded', value: '2022', icon: 'calendar_today' }, { label: 'Team Members', value: '48+', icon: 'groups' }, { label: 'Countries', value: '12', icon: 'public' }, { label: 'Enterprise Clients', value: '200+', icon: 'business' }].map((stat, i) => (
              <div key={i} className="text-center group" style={{ transitionDelay: i * 100 + 'ms' }}>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 mb-4 group-hover:bg-primary/20 transition-colors">
                  <span className="material-symbols-outlined text-primary text-xl">{stat.icon}</span>
                </div>
                <div className="text-4xl lg:text-5xl font-extrabold mb-2 bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>{stat.value}</div>
                <p className="text-gray-400 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Solve */}
      <section ref={solveRef} className="py-28 bg-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${solveInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-4">The Problem</span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4">What We Solve</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">Modern enterprises are drowning in complexity. We built OrbitMind to fix the three root causes.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {problems.map((problem, i) => (
              <div key={i} className={`group bg-white/[0.03] border border-white/10 rounded-2xl p-8 hover:border-primary/40 hover:bg-white/[0.06] hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(19,164,236,0.12)] transition-all duration-500 ${solveInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: i * 120 + 'ms' }}>
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>
                  <span className="material-symbols-outlined text-white text-2xl">{problem.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{problem.title}</h3>
                <p className="text-gray-400 leading-relaxed text-sm">{problem.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section ref={philRef} className="py-28 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(19,164,236,0.08) 0%, rgba(124,58,237,0.08) 100%)' }} />
        <div className="absolute inset-0 border-y border-white/5" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className={`transition-all duration-700 ${philInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <div className="text-[120px] font-black leading-none mb-2 bg-clip-text text-transparent select-none" style={{ backgroundImage: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }} aria-hidden="true">"</div>
              <blockquote className="text-2xl lg:text-3xl font-medium text-white italic leading-relaxed mb-8">
                We believe the next generation of industry leaders won't just use AI — they will be built upon it. Our systems are designed for machines to execute and humans to supervise, shifting the paradigm from labor-intensive to insight-driven.
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-sm flex-shrink-0" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>JR</div>
                <div>
                  <p className="text-white font-bold">Jordan Reeves</p>
                  <p className="text-gray-500 text-sm">Founder & CEO, OrbitMind</p>
                </div>
              </div>
            </div>
            <div className={`flex flex-col gap-6 transition-all duration-700 delay-200 ${philInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              {philosophyPillars.map((pillar, i) => (
                <div key={i} className="flex items-start gap-5 bg-white/[0.04] border border-white/10 rounded-2xl p-6 hover:border-primary/30 hover:bg-white/[0.07] transition-all duration-300 group" style={{ transitionDelay: i * 100 + 'ms' }}>
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>
                    <span className="material-symbols-outlined text-white text-xl">{pillar.icon}</span>
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">{pillar.title}</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">{pillar.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section ref={compareRef} className="py-28 bg-dark">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${compareInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-4">Scope</span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4">Defining Our Scope</h2>
            <p className="text-gray-400 text-lg">Clarity on what we do — and what we don't.</p>
          </div>
          <div className={`rounded-2xl border border-white/10 overflow-hidden transition-all duration-700 delay-200 ${compareInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="grid grid-cols-2">
              <div className="bg-primary/10 border-r border-white/10 p-6 flex items-center justify-center"><h4 className="text-primary font-bold uppercase tracking-widest text-sm">What We Do</h4></div>
              <div className="bg-white/[0.02] p-6 flex items-center justify-center"><h4 className="text-gray-500 font-bold uppercase tracking-widest text-sm">What We Don't Do</h4></div>
            </div>
            {whatWeDo.map((item, i) => (
              <div key={i} className={`grid grid-cols-2 border-t border-white/10 ${i % 2 === 0 ? 'bg-white/[0.02]' : 'bg-transparent'}`}>
                <div className="p-6 border-r border-white/10 flex gap-3 items-start">
                  <span className="text-emerald-400 font-bold mt-0.5 flex-shrink-0">✓</span>
                  <span className="text-white text-sm leading-relaxed">{item}</span>
                </div>
                <div className="p-6 flex gap-3 items-start">
                  <span className="text-red-400 font-bold mt-0.5 flex-shrink-0">✗</span>
                  <span className="text-gray-400 text-sm leading-relaxed">{whatWeDont[i]}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section ref={industriesRef} className="py-28 bg-[#0b1620]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${industriesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-4">Verticals</span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4">Industries We Transform</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">Scalable AI solutions engineered for the world's most complex sectors.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, i) => (
              <div key={i} className={`group bg-white/[0.03] border border-white/10 rounded-2xl p-8 hover:border-primary/40 hover:bg-white/[0.06] hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(19,164,236,0.15)] transition-all duration-500 ${industriesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: i * 80 + 'ms' }}>
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-5 group-hover:scale-110 transition-transform duration-300" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>
                  <span className="material-symbols-outlined text-white text-2xl">{industry.icon}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{industry.name}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{industry.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section ref={valuesRef} className="py-28 bg-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${valuesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-4">Culture</span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4">Built on strong values</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">The principles that guide every decision we make, every line of code we write.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <div key={i} className={`group bg-white/[0.03] border border-white/10 rounded-2xl p-8 text-center hover:border-primary/40 hover:bg-white/[0.06] hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(19,164,236,0.12)] transition-all duration-500 ${valuesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: i * 100 + 'ms' }}>
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-5 group-hover:scale-110 transition-transform duration-300" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>
                  <span className="material-symbols-outlined text-white text-2xl">{value.icon}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{value.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="py-28 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(19,164,236,0.18) 0%, rgba(124,58,237,0.12) 40%, transparent 70%)' }} />
        <div className="absolute inset-0 border-y border-white/5" />
        <div className={`relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${ctaInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-6">Get Started</span>
          <h2 className="text-4xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">
            Ready to join the{' '}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}>AI revolution?</span>
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">Join 200+ enterprise clients already running on OrbitMind. Start automating today.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/signup" className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white text-base transition-all duration-300 hover:scale-105 hover:shadow-2xl" style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)', boxShadow: '0 0 30px rgba(19,164,236,0.4)' }}>
              <span>Get Started Free</span>
              <span className="material-symbols-outlined text-lg transition-transform group-hover:translate-x-1">arrow_forward</span>
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white text-base border border-white/20 bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-primary/50 transition-all duration-300 hover:scale-105">
              <span className="material-symbols-outlined text-lg text-primary">calendar_month</span>
              <span>Book a Demo</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
